import { ConsolidatedLocationView, LocationEntityLink } from '@mark43/cad-api';
import { v4 } from 'uuid';
import { LocationStoreView } from '../types/locations';

/**
 * Pulls the dispatch areas out of the entity links for each result in a location search. This handles cases
 * for normal results and those where the entity links were manually resolved (generally Esri results).
 *
 * Police dispatch areas are presented first, followed by fire dispatch events.
 *
 * @param results The unmapped results.
 * @returns The mapped results, now including a dispatchArea field.
 */
export function mapDispatchAreasForLocationSearch(
    results: ConsolidatedLocationView[]
): LocationStoreView[] {
    return results.map(mapConsolidatedLocationViewToLocationStoreView);
}

export function mapConsolidatedLocationViewToLocationStoreView(
    location: ConsolidatedLocationView | LocationStoreView,
    id: number | undefined = 0
): LocationStoreView {
    const entityLink: LocationEntityLink | undefined = location.entityLinks?.[0] || {};

    const { policeDispatchArea, fireDispatchArea, medicalDispatchArea } = entityLink;
    const dispatchAreas = [];
    const dispatchAreaClasses = [];

    if (policeDispatchArea) {
        dispatchAreas.push(policeDispatchArea);
        dispatchAreaClasses.push('Police');
    }
    if (fireDispatchArea) {
        dispatchAreas.push(fireDispatchArea);
        dispatchAreaClasses.push('Fire');
    }
    if (medicalDispatchArea) {
        dispatchAreas.push(medicalDispatchArea);
        dispatchAreaClasses.push('EMS');
    }

    return {
        ...location,
        id, // This is ok because we always replace all search results in the store completely.
        dispatchArea: dispatchAreas.join(' / '),
        dispatchAreaLabel: dispatchAreaClasses.join(' / '),
        isForcedResult: 'isForcedResult' in location ? location.isForcedResult : false,
        isForcedSubpremiseResult: location.resolverSource === 'FORCED_SUB_PREMISES',
        source: location.resolverSource === 'LAT_LONG' ? 'LAT_LONG' : location.source,
        uuid: v4(),
    };
}
